export const REPLAY_VIDEO_URL = null; // [[REPLAY VIDEO PENDING — paste hosted Vimeo/Wistia/mp4 URL here]]

export const RCOPY = {
  en: {
    nav: { watch: "WATCH", method: "THE METHOD", host: "HOST", faq: "FAQ", book: "BOOK MY SESSION ↗" },
    eyebrow: "YOUR REPLAY IS READY — WATCH IT NOW",
    h1: ["LEARN HOW TO BUY YOUR", "FIRST HOME STRATEGICALLY", "— NOT EMOTIONALLY"],
    sub: "Watch the full session below, then book a free follow-up conversation with Plinio to walk through your specific options — in your own language, no obligation.",
    quote: "The problem was never your credit score. It's that no one ever explained your real options.",
    availability: "⏳ REPLAY AVAILABLE NOW — NO EXPIRATION",
    cta: "✓ BOOK MY FREE FOLLOW-UP CONVERSATION",
    trust: "Free · No obligation · No hard credit pull required",
    videoPending: "[[REPLAY VIDEO PENDING — swap URL in replayCopy.js]]",
    videoPendingSub: "FULL SESSION RECORDING · 5 MODULES · EN / ES",
    ticker: ["THE STRATEGIC BUYER SESSION", "FREE FOLLOW-UP CONVERSATION", "NY · NJ · CT · PA · FL", "NO HARD CREDIT PULL", "FHA · CONVENTIONAL", "EN ESPAÑOL"],
    manifesto: {
      label: "WHAT YOU JUST LEARNED",
      lines: [
        { t: "YOU DON'T NEED", c: "w" },
        { t: "PERFECT CREDIT.", c: "m" },
        { t: "YOU NEED", c: "w" },
        { t: "THE RIGHT PLAN.", c: "a" },
      ],
      body: "You saw what documented income and credit readiness mean in practice, and how FHA and Conventional programs approach a first-time buyer differently. A buyer who knows their options can't be rushed into the wrong one.",
      cta: "SEE HOW IT APPLIES TO YOU →",
    },
    method: {
      label: "THE STRATEGIC BUYER METHOD",
      steps: [
        { n: "01", t: "UNDERSTAND WHERE YOU STAND", d: "Review what documented income and credit readiness mean for your situation." },
        { n: "02", t: "EXPLORE YOUR OPTIONS", d: "Walk through FHA and Conventional program considerations, explained in plain language." },
        { n: "03", t: "GET A CLEAR NEXT STEP", d: "A personalized recap of your options, with no pressure to decide on the spot." },
      ],
    },
    stack: {
      label: "",
      h2: "HERE'S WHAT'S WAITING WHEN YOU BOOK",
      items: [
        "A 1:1 conversation with Plinio to review your specific loan options",
        "A plain-language walkthrough of FHA and Conventional considerations for your situation",
        "A documentation checklist for what to prepare next",
        "Answers to your questions, in your own language",
        "No obligation, no hard credit pull to talk",
      ],
      reveal: "Your time investment: about 30 minutes and your honesty.",
    },
    guarantee: {
      label: "",
      title: "THE STRATEGIC BUYER COMMITMENT",
      ph: "[[COMPLIANCE APPROVAL REQUIRED — exact wording pending brokerage sign-off]]",
      draft: "Draft pattern once approved: \u201CComplete your session and if you don't walk away with a clear picture of your options, I'll keep working with you at no additional cost until you do.\u201D",
      smallPrint: "Service guarantee only. Not a guarantee of loan approval, rate, savings, or funding.",
    },
    hostLabel: "",
    objections: {
      label: "",
      h2: "Still on the fence?",
      pains: [
        "\u201CI need perfect credit before I can even talk to someone\u201D — readiness isn't a single number. The only way to know where you stand is to look at your real situation, not to guess.",
        "\u201CI should just take the first offer\u201D — that's the emotional decision this session was built to prevent. Comparing costs you nothing; skipping it can cost you plenty.",
      ],
      changesLabel: "HERE'S WHAT HAPPENS WHEN YOU BOOK:",
      bullets: [
        "We review your specific situation together",
        "You see the full menu of loan options that apply to you — not just one",
        "You leave with a clear next step, in your own language, whether now or later",
      ],
    },
    faq: {
      label: "",
      h2: "COMMON QUESTIONS",
      items: [
        { q: "Is the follow-up conversation really free?", a: "Yes — completely free. It's a 1:1 educational conversation to review your options. There is no fee and nothing to buy." },
        { q: "Will booking hurt my credit?", a: "No. Booking and having the conversation involves no hard credit pull. You're simply talking through your situation and options." },
        { q: "What if the honest answer is \u201Cnot yet\u201D?", a: "Then you'll leave with a clear preparation plan — what to document, what to strengthen, and when to revisit. No pressure, no timeline pushed on you." },
        { q: "Do I have to work with Plinio for my loan?", a: "No. The conversation creates zero obligation. You're free to take what you learn and proceed with anyone, on your own timeline." },
        { q: "What if I don't speak English well — is this available in Spanish?", a: "Yes. The entire conversation can be held in Spanish — that's a core part of why the Strategic Buyer Session exists." },
        { q: "How long is the replay available?", a: "The replay is currently available with no expiration. The follow-up conversation calendar, however, fills on a weekly basis — booking early gets you a better slot." },
        { q: "What happens after I book?", a: "You'll receive a confirmation email with the details. The conversation itself is about 30 minutes: your situation, your options, and a clear next step." },
        { q: "What areas do you serve?", a: "New York, New Jersey, Connecticut, Pennsylvania & Florida." },
      ],
    },
    booking: {
      label: "",
      h2a: "YOUR NEXT MOVE STARTS WITH ",
      h2b: "A CLEARER MAP.",
      bullets: [
        "100% free follow-up conversation",
        "No hard credit pull to book or to talk",
        "Held in English or Spanish — your choice",
        "About 30 minutes · no obligation to proceed",
      ],
      cardLabel: "BOOK YOUR FREE CONVERSATION",
      form: {
        firstName: "First name", lastName: "Last name", email: "Email address", phone: "Mobile phone",
        readiness: "Which best describes you?",
        readinessOptions: ["Ready within 90 days", "Ready within 180 days", "Just exploring"],
        consent: "I agree to receive email and SMS communications about scheduling this conversation. Message and data rates may apply. Consent is not a condition of any purchase or loan. Reply STOP to unsubscribe.",
        submit: "✓ BOOK MY FREE CONVERSATION",
        submitting: "BOOKING...",
        errors: { required: "Please complete all required fields.", consent: "Please check the consent box to book.", server: "Something went wrong. Please try again." },
        success: {
          tag: "REQUEST RECEIVED",
          title: "YOU'RE BOOKED IN.",
          body: "Check your email — you'll receive a confirmation with the scheduling details for your free follow-up conversation with Plinio. About 30 minutes, in English or Spanish, no obligation.",
        },
      },
    },
  },

  es: {
    nav: { watch: "VER", method: "EL MÉTODO", host: "ANFITRIÓN", faq: "PREGUNTAS", book: "RESERVE SU SESIÓN ↗" },
    eyebrow: "SU REPETICIÓN ESTÁ LISTA — VÉALA AHORA",
    h1: ["APRENDA A COMPRAR SU", "PRIMERA CASA CON ESTRATEGIA", "— NO CON EMOCIÓN"],
    sub: "Vea la sesión completa abajo y luego reserve una conversación gratuita de seguimiento con Plinio para repasar sus opciones específicas — en su propio idioma, sin compromiso.",
    quote: "El problema nunca fue su puntaje de crédito. Es que nadie le explicó sus opciones reales.",
    availability: "⏳ REPETICIÓN DISPONIBLE AHORA — SIN EXPIRACIÓN",
    cta: "✓ RESERVAR MI CONVERSACIÓN GRATUITA",
    trust: "Gratis · Sin compromiso · No se requiere consulta dura de crédito",
    videoPending: "[[REPLAY VIDEO PENDING — swap URL in replayCopy.js]]",
    videoPendingSub: "GRABACIÓN COMPLETA · 5 MÓDULOS · EN / ES",
    ticker: ["LA SESIÓN DEL COMPRADOR ESTRATÉGICO", "CONVERSACIÓN GRATUITA DE SEGUIMIENTO", "NY · NJ · CT · PA · FL", "SIN CONSULTA DURA DE CRÉDITO", "FHA · CONVENCIONAL", "EN ESPAÑOL"],
    manifesto: {
      label: "LO QUE ACABAS DE APRENDER",
      lines: [
        { t: "USTED NO NECESITA", c: "w" },
        { t: "CRÉDITO PERFECTO.", c: "m" },
        { t: "NECESITA", c: "w" },
        { t: "EL PLAN CORRECTO.", c: "a" },
      ],
      body: "Usted vio lo que ingresos documentados y preparación de crédito significan en la práctica, y cómo el préstamo FHA y el préstamo convencional evalúan distinto a un comprador de vivienda por primera vez. A un comprador que conoce sus opciones no lo pueden presionar hacia la equivocada.",
      cta: "VEA CÓMO APLICA A USTED →",
    },
    method: {
      label: "EL MÉTODO DEL COMPRADOR ESTRATÉGICO",
      steps: [
        { n: "01", t: "ENTIENDA DÓNDE ESTÁ", d: "Repase lo que ingresos documentados y preparación de crédito significan para su situación." },
        { n: "02", t: "EXPLORE SUS OPCIONES", d: "Recorra las consideraciones del préstamo FHA y el préstamo convencional, explicadas en lenguaje sencillo." },
        { n: "03", t: "OBTENGA UN SIGUIENTE PASO CLARO", d: "Un resumen personalizado de sus opciones, sin presión de decidir en el momento." },
      ],
    },
    stack: {
      label: "",
      h2: "ESTO ES LO QUE LE ESPERA AL RESERVAR",
      items: [
        "Una conversación 1-a-1 con Plinio para revisar sus opciones de préstamo específicas",
        "Un recorrido en lenguaje sencillo por el préstamo FHA y el préstamo convencional para su situación",
        "Una lista de documentos para saber qué preparar después",
        "Respuestas a sus preguntas, en su propio idioma",
        "Sin compromiso, sin consulta dura de crédito para hablar",
      ],
      reveal: "Su inversión de tiempo: unos 30 minutos y su honestidad.",
    },
    guarantee: {
      label: "",
      title: "EL COMPROMISO DEL COMPRADOR ESTRATÉGICO",
      ph: "[[COMPLIANCE APPROVAL REQUIRED — exact wording pending brokerage sign-off]]",
      draft: "Patrón preliminar una vez aprobado: \u201CComplete su sesión y, si no sale con una imagen clara de sus opciones, seguiré trabajando con usted sin costo adicional hasta que la tenga.\u201D",
      smallPrint: "Garantía de servicio únicamente. No es una garantía de aprobación de préstamo, tasa, ahorro ni financiamiento.",
    },
    hostLabel: "",
    objections: {
      label: "",
      h2: "¿Todavía lo está pensando?",
      pains: [
        "\u201CNecesito crédito perfecto antes de siquiera hablar con alguien\u201D — la preparación no es un solo número. La única forma de saber dónde está es mirar su situación real, no adivinar.",
        "\u201CDebería aceptar la primera oferta\u201D — esa es la decisión emocional que esta sesión previene. Comparar no le cuesta nada; omitirlo puede costarle mucho.",
      ],
      changesLabel: "ESTO ES LO QUE PASA CUANDO USTED RESERVA:",
      bullets: [
        "Revisamos su situación específica juntos",
        "Verá el menú completo de opciones de préstamo que aplican a usted — no solo una",
        "Saldrá con un siguiente paso claro, en su propio idioma, sea ahora o después",
      ],
    },
    faq: {
      label: "",
      h2: "PREGUNTAS FRECUENTES",
      items: [
        { q: "¿La conversación de seguimiento es realmente gratis?", a: "Sí — completamente gratis. Es una conversación educativa 1-a-1 para revisar sus opciones. No hay tarifa y no hay nada que comprar." },
        { q: "¿Reservar afectará mi crédito?", a: "No. Reservar y tener la conversación no implica ninguna consulta dura de crédito. Usted simplemente conversa sobre su situación y sus opciones." },
        { q: "¿Y si la respuesta honesta es \u201Ctodavía no\u201D?", a: "Entonces saldrá con un plan de preparación claro — qué documentar, qué fortalecer y cuándo retomarlo. Sin presión, sin plazos impuestos." },
        { q: "¿Tengo que trabajar con Plinio para mi préstamo?", a: "No. La conversación no crea ninguna obligación. Usted es libre de tomar lo que aprenda y avanzar con quien desee, a su propio ritmo." },
        { q: "¿Y si no hablo bien inglés — está disponible en español?", a: "Sí. Toda la conversación puede ser en español — esa es una parte central de por qué existe la Sesión del Comprador Estratégico." },
        { q: "¿Por cuánto tiempo está disponible la repetición?", a: "La repetición está disponible actualmente sin expiración. Sin embargo, el calendario de conversaciones se llena cada semana — reservar temprano le da un mejor horario." },
        { q: "¿Qué pasa después de reservar?", a: "Recibirá un correo de confirmación con los detalles. La conversación dura unos 30 minutos: su situación, sus opciones y un siguiente paso claro." },
        { q: "¿Qué áreas atienden?", a: "New York, New Jersey, Connecticut, Pennsylvania y Florida." },
      ],
    },
    booking: {
      label: "",
      h2a: "SU PRÓXIMO PASO EMPIEZA CON ",
      h2b: "UN MAPA MÁS CLARO.",
      bullets: [
        "Conversación de seguimiento 100% gratis",
        "Sin consulta dura de crédito para reservar ni para hablar",
        "En inglés o español — tú eliges",
        "Unos 30 minutos · sin obligación de continuar",
      ],
      cardLabel: "RESERVE SU CONVERSACIÓN GRATUITA",
      form: {
        firstName: "Nombre", lastName: "Apellido", email: "Correo electrónico", phone: "Teléfono móvil",
        readiness: "¿Cuál lo describe mejor?",
        readinessOptions: ["Listo en 90 días", "Listo en 180 días", "Solo explorando"],
        consent: "Acepto recibir comunicaciones por correo y SMS sobre la programación de esta conversación. Pueden aplicar tarifas de mensajes y datos. El consentimiento no es condición de compra ni de préstamo. Responde STOP para cancelar.",
        submit: "✓ RESERVAR MI CONVERSACIÓN GRATIS",
        submitting: "RESERVANDO...",
        errors: { required: "Por favor completa todos los campos requeridos.", consent: "Marca la casilla de consentimiento para reservar.", server: "Algo salió mal. Inténtalo de nuevo." },
        success: {
          tag: "SOLICITUD RECIBIDA",
          title: "ESTÁS RESERVADO.",
          body: "Revise su correo — recibirá una confirmación con los detalles para programar su conversación gratuita de seguimiento con Plinio. Unos 30 minutos, en inglés o español, sin compromiso.",
        },
      },
    },
  },
};
